import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  Loader2Icon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';

interface Profession {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  show_plans_tab: boolean;
  show_measurements_tab: boolean;
  show_blood_parameters_tab: boolean;
  show_custom_intake_tab: boolean;
  show_supplements_tab: boolean;
  label_clients: string;
  label_plans: string;
  label_measurements: string;
  label_blood_parameters: string;
  label_supplements: string;
  is_system_template: boolean;
}

interface FormData {
  name: string;
  description: string;
  color: string;
  icon: string;
  show_plans_tab: boolean;
  show_measurements_tab: boolean;
  show_blood_parameters_tab: boolean;
  show_custom_intake_tab: boolean;
  show_supplements_tab: boolean;
  label_clients: string;
  label_plans: string;
  label_measurements: string;
  label_blood_parameters: string;
  label_supplements: string;
}

interface FormErrors {
  [key: string]: string;
}

// Preset colors for quick selection
const PRESET_COLORS = [
  { name: 'Green', value: '#5B8E87' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Purple', value: '#9333EA' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Amber', value: '#F59E0B' },
];

// Preset emojis for quick selection
const PRESET_EMOJIS = ['🥗', '💪', '🎯', '🧘', '🏃', '❤️', '🌟', '📊', '🎓', '💼'];

export function ProfessionEdit() {
  const navigate = useNavigate();
  const { professionId } = useParams<{ professionId: string }>();
  const { session, profile } = useAuth();
  const isCreate = !professionId;

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    color: '#5B8E87',
    icon: '',
    show_plans_tab: true,
    show_measurements_tab: true,
    show_blood_parameters_tab: true,
    show_custom_intake_tab: false,
    show_supplements_tab: true,
    label_clients: 'Clients',
    label_plans: 'Plans',
    label_measurements: 'Measurements',
    label_blood_parameters: 'Blood Parameters',
    label_supplements: 'Supplements',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState(formData.color);

  // Fetch profession data if editing
  const { data: professionData, isLoading } = useQuery({
    queryKey: ['admin', 'profession', professionId],
    queryFn: async () => {
      const response = await apiRequest(
        '/api/admin/professions',
        {},
        session?.access_token
      );
      const professions = response.professions as Profession[];
      return professions.find((p) => p.id === professionId);
    },
    enabled: !isCreate && !!session?.access_token && !!professionId,
  });

  // Initialize form data when profession data is loaded
  useEffect(() => {
    if (professionData) {
      setFormData({
        name: professionData.name,
        description: professionData.description || '',
        color: professionData.color,
        icon: professionData.icon || '',
        show_plans_tab: professionData.show_plans_tab,
        show_measurements_tab: professionData.show_measurements_tab,
        show_blood_parameters_tab: professionData.show_blood_parameters_tab ?? true,
        show_custom_intake_tab: professionData.show_custom_intake_tab,
        show_supplements_tab: professionData.show_supplements_tab ?? true,
        label_clients: professionData.label_clients,
        label_plans: professionData.label_plans,
        label_measurements: professionData.label_measurements,
        label_blood_parameters: professionData.label_blood_parameters || 'Blood Parameters',
        label_supplements: professionData.label_supplements || 'Supplements',
      });
      setCustomColor(professionData.color);
    }
  }, [professionData]);

  // Update custom color when formData.color changes
  useEffect(() => {
    setCustomColor(formData.color);
  }, [formData.color]);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name should be under 100 characters';
    }

    // Description validation
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description should be under 500 characters';
    }

    // Color validation
    const colorRegex = /^#[0-9A-F]{6}$/i;
    if (!colorRegex.test(formData.color)) {
      newErrors.color = 'Enter a valid hex color (e.g., #5B8E87)';
    }

    // Icon validation
    if (formData.icon && formData.icon.length > 10) {
      newErrors.icon = 'Icon should be a single emoji or short text';
    }

    // Label validations
    if (!formData.label_clients.trim()) {
      newErrors.label_clients = 'Clients label is required';
    } else if (formData.label_clients.length > 50) {
      newErrors.label_clients = 'Label should be under 50 characters';
    }

    if (!formData.label_plans.trim()) {
      newErrors.label_plans = 'Plans label is required';
    } else if (formData.label_plans.length > 50) {
      newErrors.label_plans = 'Label should be under 50 characters';
    }

    if (!formData.label_measurements.trim()) {
      newErrors.label_measurements = 'Measurements label is required';
    } else if (formData.label_measurements.length > 50) {
      newErrors.label_measurements = 'Label should be under 50 characters';
    }

    if (!formData.label_blood_parameters.trim()) {
      newErrors.label_blood_parameters = 'Blood Parameters label is required';
    } else if (formData.label_blood_parameters.length > 50) {
      newErrors.label_blood_parameters = 'Label should be under 50 characters';
    }

    if (!formData.label_supplements.trim()) {
      newErrors.label_supplements = 'Supplements label is required';
    } else if (formData.label_supplements.length > 50) {
      newErrors.label_supplements = 'Label should be under 50 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!session?.access_token) {
      setErrors({ general: 'No authentication token found. Please log in again.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const url = isCreate
        ? '/api/admin/professions'
        : `/api/admin/professions/${professionId}`;

      const method = isCreate ? 'POST' : 'PATCH';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error) {
          // Handle specific field errors
          if (data.details) {
            setErrors(data.details.fieldErrors || {});
          } else {
            // General error
            setErrors({ general: data.error });
          }
        }
        throw new Error(data.error || 'Failed to save profession');
      }

      // Success - navigate back to professions list
      navigate('/admin/professions');
    } catch (error) {
      console.error('Save profession error:', error);
      setErrors({
        general: error instanceof Error ? error.message : 'Failed to save profession',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Access control
  if (!profile || profile.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md">
          <p className="text-lg font-semibold text-gray-800 mb-2">Access Denied</p>
          <p className="text-gray-600 mb-6">You need admin privileges to access this page.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profession...</p>
        </div>
      </div>
    );
  }

  const isSystemTemplate = professionData?.is_system_template || false;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/professions')}
              className="text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">
              {isCreate ? 'Add Custom Profession' : `Edit ${professionData?.name}`}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm">
          {/* System Template Notice */}
          {isSystemTemplate && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  This is a system template. You can customize it, but changes will only apply
                  to your organization.
                </p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{errors.general}</p>
              </div>
            )}

            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#212121] flex items-center gap-2">
                <span className="w-8 h-8 bg-[#5B8E87] bg-opacity-10 rounded-lg flex items-center justify-center text-[#5B8E87] text-sm font-bold">
                  1
                </span>
                Basic Information
              </h3>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profession Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Dietitian, Gym Trainer"
                  maxLength={100}
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all resize-none ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Brief description of this profession"
                  rows={3}
                  maxLength={500}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.description ? (
                    <p className="text-sm text-red-600">{errors.description}</p>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {formData.description.length}/500 characters
                    </p>
                  )}
                </div>
              </div>

              {/* Icon and Color Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon (Emoji)
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => handleInputChange('icon', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all text-center text-2xl ${
                      errors.icon ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="🥗"
                    maxLength={10}
                  />
                  {errors.icon && <p className="mt-1 text-sm text-red-600">{errors.icon}</p>}
                  {/* Preset Emojis */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {PRESET_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handleInputChange('icon', emoji)}
                        className={`w-10 h-10 rounded-lg border-2 text-xl hover:bg-gray-50 transition-colors ${
                          formData.icon === emoji
                            ? 'border-[#5B8E87] bg-green-50'
                            : 'border-gray-200'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color *
                  </label>
                  <div className="space-y-2">
                    {/* Color Preview and Input */}
                    <div className="flex gap-2">
                      <div
                        className="w-12 h-12 rounded-xl border-2 border-gray-300 flex-shrink-0 cursor-pointer"
                        style={{ backgroundColor: formData.color }}
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        title="Click to toggle color picker"
                      />
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) =>
                          handleInputChange('color', e.target.value.toUpperCase())
                        }
                        className={`flex-1 px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all font-mono ${
                          errors.color ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="#5B8E87"
                        maxLength={7}
                      />
                    </div>
                    {errors.color && <p className="text-sm text-red-600">{errors.color}</p>}

                    {/* Preset Colors */}
                    <div className="flex flex-wrap gap-2">
                      {PRESET_COLORS.map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => handleInputChange('color', preset.value)}
                          className={`w-10 h-10 rounded-lg border-2 hover:scale-110 transition-transform ${
                            formData.color === preset.value
                              ? 'border-gray-800'
                              : 'border-gray-200'
                          }`}
                          style={{ backgroundColor: preset.value }}
                          title={preset.name}
                        />
                      ))}
                    </div>

                    {/* HTML5 Color Picker */}
                    {showColorPicker && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          Custom Color Picker
                        </label>
                        <input
                          type="color"
                          value={customColor}
                          onChange={(e) => {
                            setCustomColor(e.target.value);
                            handleInputChange('color', e.target.value.toUpperCase());
                          }}
                          className="w-full h-12 rounded-lg cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Configuration Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-[#212121] flex items-center gap-2">
                <span className="w-8 h-8 bg-[#5B8E87] bg-opacity-10 rounded-lg flex items-center justify-center text-[#5B8E87] text-sm font-bold">
                  2
                </span>
                Tab Configuration
              </h3>

              <p className="text-sm text-gray-600">
                Choose which tabs to show in the client details view. Messages and Appointments
                are always enabled.
              </p>

              {/* Tab Toggles */}
              <div className="space-y-3">
                {/* Plans Tab */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.show_plans_tab
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {formData.show_plans_tab ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        <XCircleIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Plans Tab</p>
                      <p className="text-sm text-gray-600">
                        Show diet plans, workout plans, or similar content
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange('show_plans_tab', !formData.show_plans_tab)
                    }
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      formData.show_plans_tab ? 'bg-[#5B8E87]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        formData.show_plans_tab ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Measurements Tab */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.show_measurements_tab
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {formData.show_measurements_tab ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        <XCircleIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Measurements Tab</p>
                      <p className="text-sm text-gray-600">
                        Track body measurements, fitness metrics, or progress data
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange(
                        'show_measurements_tab',
                        !formData.show_measurements_tab
                      )
                    }
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      formData.show_measurements_tab ? 'bg-[#5B8E87]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        formData.show_measurements_tab ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Blood Parameters Tab */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.show_blood_parameters_tab
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {formData.show_blood_parameters_tab ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        <XCircleIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Blood Parameters Tab</p>
                      <p className="text-sm text-gray-600">
                        Track blood test results, lab values, and health markers
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange(
                        'show_blood_parameters_tab',
                        !formData.show_blood_parameters_tab
                      )
                    }
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      formData.show_blood_parameters_tab ? 'bg-[#5B8E87]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        formData.show_blood_parameters_tab ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Custom Intake Tab */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.show_custom_intake_tab
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {formData.show_custom_intake_tab ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        <XCircleIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Custom Intake Tab</p>
                      <p className="text-sm text-gray-600">
                        Show custom intake forms or questionnaires
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange(
                        'show_custom_intake_tab',
                        !formData.show_custom_intake_tab
                      )
                    }
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      formData.show_custom_intake_tab ? 'bg-[#5B8E87]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        formData.show_custom_intake_tab ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Supplements Tab */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.show_supplements_tab
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {formData.show_supplements_tab ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        <XCircleIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Supplements Tab</p>
                      <p className="text-sm text-gray-600">
                        Manage supplements, prescriptions, and inventory for clients
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange(
                        'show_supplements_tab',
                        !formData.show_supplements_tab
                      )
                    }
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      formData.show_supplements_tab ? 'bg-[#5B8E87]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        formData.show_supplements_tab ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Custom Labels Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-[#212121] flex items-center gap-2">
                <span className="w-8 h-8 bg-[#5B8E87] bg-opacity-10 rounded-lg flex items-center justify-center text-[#5B8E87] text-sm font-bold">
                  3
                </span>
                Custom Labels
              </h3>

              <p className="text-sm text-gray-600">
                Customize terminology to match your profession. For example, "Members" instead
                of "Clients" for gym trainers.
              </p>

              <div className="space-y-4">
                {/* Clients Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clients Label *
                  </label>
                  <input
                    type="text"
                    value={formData.label_clients}
                    onChange={(e) => handleInputChange('label_clients', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all ${
                      errors.label_clients ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Clients, Members, Patients"
                    maxLength={50}
                  />
                  {errors.label_clients && (
                    <p className="mt-1 text-sm text-red-600">{errors.label_clients}</p>
                  )}
                </div>

                {/* Plans Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plans Label *
                  </label>
                  <input
                    type="text"
                    value={formData.label_plans}
                    onChange={(e) => handleInputChange('label_plans', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all ${
                      errors.label_plans ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Diet Plans, Workout Plans, Programs"
                    maxLength={50}
                  />
                  {errors.label_plans && (
                    <p className="mt-1 text-sm text-red-600">{errors.label_plans}</p>
                  )}
                </div>

                {/* Measurements Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Measurements Label *
                  </label>
                  <input
                    type="text"
                    value={formData.label_measurements}
                    onChange={(e) => handleInputChange('label_measurements', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all ${
                      errors.label_measurements ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Body Measurements, Fitness Metrics, Progress"
                    maxLength={50}
                  />
                  {errors.label_measurements && (
                    <p className="mt-1 text-sm text-red-600">{errors.label_measurements}</p>
                  )}
                </div>

                {/* Blood Parameters Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Parameters Label *
                  </label>
                  <input
                    type="text"
                    value={formData.label_blood_parameters}
                    onChange={(e) => handleInputChange('label_blood_parameters', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all ${
                      errors.label_blood_parameters ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Blood Parameters, Lab Results, Health Markers"
                    maxLength={50}
                  />
                  {errors.label_blood_parameters && (
                    <p className="mt-1 text-sm text-red-600">{errors.label_blood_parameters}</p>
                  )}
                </div>

                {/* Supplements Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplements Label *
                  </label>
                  <input
                    type="text"
                    value={formData.label_supplements}
                    onChange={(e) => handleInputChange('label_supplements', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent transition-all ${
                      errors.label_supplements ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Supplements, Prescriptions, Products"
                    maxLength={50}
                  />
                  {errors.label_supplements && (
                    <p className="mt-1 text-sm text-red-600">{errors.label_supplements}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin/professions')}
                disabled={isSubmitting}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-medium hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#4F7A74] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2Icon className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>{isCreate ? 'Create Profession' : 'Save Changes'}</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
